import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit, ViewChild, ElementRef, computed, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { slideDownUp } from '../../shared/animations';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { NgClass } from '@angular/common';
import { MenuModule } from 'headlessui-angular';
import { SharedModule } from 'src/app/shared/shared.module';
import { AuthService } from 'src/app/auth/auth.service';
import { ImageService } from 'src/app/core/service/image.service';
import { ModuleAccessService } from 'src/app/core/service/module-access.service';
import { SubModuleAccessService } from 'src/app/core/service/sub-module-access.service';
import { PermissionService } from 'src/app/core/service/permission.service';
import { ComponentDeactivateService } from 'src/app/core/service/component-deactivate.service';
@Component({
    selector: 'sidebar',
    templateUrl: "./sidebar.component.html",
    animations: [
        slideDownUp,
        trigger('toggleAnimation', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-10px)' }),
                animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
            ]),
            transition(':leave', [animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' }))]),
        ]),
    ],
    standalone: true,
    imports: [NgClass, RouterLink, NgScrollbarModule, RouterLinkActive, TranslateModule, MenuModule, SharedModule],
})
export class SidebarComponent implements OnInit, OnDestroy {
    active = false;
    store: any;
    activeDropdown: string[] = [];
    parentDropdown: string = '';
    profile_picture: string = '';
    profileImageUrl: string = '';
    profile_name: string = '';
    profile_email: string = '';
    profileAvatarLoading = false;
    userData: any;
    @ViewChild('sidebarScroll') sidebarScroll!: ElementRef;
    @ViewChild('mastersDropdown') mastersDropdown!: ElementRef;
    public readonly moduleAccessService = inject(ModuleAccessService);
    private readonly subModuleAccessService = inject(SubModuleAccessService);
    public readonly permissionService = inject(PermissionService);
  /** Admin Center nav when user has any related assigned module / sub-module. */
  readonly showAdminCenter = computed(() => {
    this.moduleAccessService.moduleCount();
    this.subModuleAccessService.parentModuleSlugs();
    return (
      this.moduleAccessService.hasAnyModuleAccess([
        "contract",
        "form-builder",
        "clause",
      ]) ||
      this.subModuleAccessService.hasSubModuleAccess(
        "event",
        "template-builder"
      ) ||
      this.subModuleAccessService.hasSubModuleAccess(
        "contract",
        "contract-template"
      ) ||
      this.subModuleAccessService.hasSubModuleAccess(
        "purchase-order",
        "purchase-order-list"
      )
    );
  });
    constructor(
        public translate: TranslateService,
        public storeData: Store<any>,
        public router: Router,
        public authService: AuthService,
        public imageService: ImageService,
        private readonly componentDeactivateService: ComponentDeactivateService,
    ) {}
    initStore() {
        this.storeData
            .select((d) => d.index)
            .subscribe((d) => {
                this.store = d;
            });
    }

    ngOnInit() {
        this.initStore();
        this.setActiveDropdown();
        // close flyout when clicking outside
        if (typeof document !== 'undefined') {
            document.addEventListener('click', this.handleDocumentClick, true);
        }
        this.userData = this.authService.getUserData();
        this.loadProfileImage();
        // Subscribe to profile updates
        this.authService.profileDetails$.subscribe((profileData) => {
            if (profileData) {
                // Update profile details from observable
                this.profile_picture = profileData.profile_picture;
                this.profile_name = profileData.name;
                this.profile_email = profileData.email_id;

                // Reload profile image if picture changed
                this.loadProfileImage();
            } else {
                // Fallback to userData if observable hasn't emitted yet
                this.profile_picture = this.userData?.profile_picture;
                this.profile_name = this.userData?.name;
                this.profile_email = this.userData?.email_id;
                this.loadProfileImage();
            }
        });
    }

    private isBrowserContext(): boolean {
        return typeof window !== 'undefined' && typeof document !== 'undefined';
    }

    setActiveDropdown() {
        if (!this.isBrowserContext()) return;
        const selector = document.querySelector('.sidebar ul a[routerLink="' + window.location.pathname + '"]');
        if (selector) {
            selector.classList.add('active');
            const ul: any = selector.closest('ul.sub-menu');
            if (ul) {
                // Previously this auto-clicked the parent nav-link to expand the accordion on load.
                // Removed to prevent auto-opening menus when the page reloads.
            }
        }
    }

    toggleMobileMenu() {
        if (window.innerWidth < 1024) {
            this.storeData.dispatch({ type: 'toggleSidebar' });
        }
    } 

    toggleAccordion(name: string, parent?: string) {
        if (this.activeDropdown.includes(name)) {
            this.activeDropdown = this.activeDropdown.filter((d) => d !== name);
        } else {
            this.activeDropdown.push(name);
        }
    }

    toggleNestedAccordion(name: string, event?: MouseEvent) {
        // First, ensure parent 'AdminMaster' is open
        const wasParentClosed = !this.activeDropdown.includes('AdminMaster');
        if (wasParentClosed) {
            this.activeDropdown.push('AdminMaster');
        }

        // Then toggle the nested accordion
        const isOpening = !this.activeDropdown.includes(name);

        if (this.activeDropdown.includes(name)) {
            this.activeDropdown = this.activeDropdown.filter((d) => d !== name);
            // Remove upward shift class from parent when closing nested menu
            this.removeParentShiftClass();
        } else {
            this.activeDropdown.push(name);
        }

        // Check if we need to expand upward and shift parent
        if (isOpening && event) {
            const delay = wasParentClosed ? 600 : 350;
            setTimeout(() => {
                this.checkNestedDropdownPosition(event.target as HTMLElement);
            }, delay);
        }
    }

    private addParentShiftClass() {
        if (!this.isBrowserContext()) return;
        const parentAccordion = document.querySelector('li.nav-item .accordion-content.side-menu.for-nested');
        if (parentAccordion) {
            parentAccordion.classList.add('shift-upward');
        }
    }

    private removeParentShiftClass() {
        if (!this.isBrowserContext()) return;
        const parentAccordion = document.querySelector('li.nav-item .accordion-content.side-menu.for-nested');
        if (parentAccordion) {
            parentAccordion.classList.remove('shift-upward');
        }
    }

    private checkNestedDropdownPosition(element: HTMLElement) {
        if (!this.sidebarScroll) return;

        const sidebarContainer = this.sidebarScroll.nativeElement;
        const liElement = element.closest('li.nested-accordion-wrapper');

        if (!liElement) return;

        // Wait for menu to render
        setTimeout(() => {
            const nestedMenu = liElement.querySelector('.nested-sub-menu') as HTMLElement;
            if (!nestedMenu) return;

            const button = liElement.querySelector('button');
            if (!button) return;

            // Get positions after menu is fully rendered
            const menuRect = nestedMenu.getBoundingClientRect();
            const containerRect = sidebarContainer.getBoundingClientRect();

            // Calculate how much of the menu goes below the visible area
            const menuBottom = menuRect.bottom;
            const containerBottom = containerRect.bottom;
            const overflowBottom = menuBottom - containerBottom;

            // If menu overflows the bottom, add shift class to parent and scroll
            if (overflowBottom > 0) {
                // Add shift class to parent accordion
                this.addParentShiftClass();

                // Add some padding (50px) for better visibility
                const scrollAmount = overflowBottom + 50;

                sidebarContainer.scrollBy({
                    top: scrollAmount,
                    behavior: 'smooth',
                });
            } else {
                // Remove shift class if no overflow
                this.removeParentShiftClass();
            }
        }, 100);
    }

    get isCollapsed(): boolean {
        if (!this.isBrowserContext()) return false;
        const nav = document.querySelector('nav.sidebar');
        return !!nav?.closest('.collapsed-default');
    }

    onNavClick(name: string, $event: MouseEvent) {
        const target = $event.target as HTMLElement;
        const sidebarEl = target.closest('nav.sidebar');
        const isCollapsed = !!sidebarEl?.closest('.collapsed-default');

        if (isCollapsed) {
            // Prevent default accordion behavior; open flyout instead
            $event.preventDefault();
            $event.stopPropagation();

            const li = target.closest('li.menu.nav-item');
            if (!li) return;

            // close other flyouts first
            this.closeAllFlyouts(sidebarEl as HTMLElement);

            // toggle this one
            li.classList.toggle('open-flyout');
            return;
        }

        // Not collapsed: behave as accordion
        this.toggleAccordion(name);
    }

    loadProfileImage() {
        let effectivePicture: string;
        if (this.profile_picture === null) {
            effectivePicture = '';
        } else if (this.profile_picture && this.profile_picture.trim() !== '') {
            effectivePicture = this.profile_picture;
        } else {
            effectivePicture = this.userData?.profile_picture || '';
        }
        this.profileAvatarLoading = true;
        if (effectivePicture && this.userData?.id) {
            this.imageService.getPresignedImageUrl('users', effectivePicture, 'profile-picture', this.userData.id).subscribe({
                next: (url) => {
                  this.profileImageUrl = url;
                  this.profileAvatarLoading = false;
                },
                error: (error) => {
                  console.error('Error loading profile image:', error);
                  this.profileAvatarLoading = false;
                }
              })
        } else {
            this.profileImageUrl = '';
        }
    }

    loadProfileDetails(name: string, email_id: string) {
        this.authService.getUserPresignedDetails(name, email_id).subscribe({
            next: (res) => {
                this.profile_email = res.email_id;
                this.profile_name = res.name;
            },
            error: (error) => {
                console.log(error);
            },
        });
    }

    private closeAllFlyouts(scope?: HTMLElement | Document) {
        if (!this.isBrowserContext()) return;
        const root: HTMLElement | Document = scope || document;
        const openItems = root.querySelectorAll('li.menu.nav-item.open-flyout');
        openItems.forEach((el) => el.classList.remove('open-flyout'));
    }

    private readonly handleDocumentClick = (ev: Event) => {
        if (!this.isBrowserContext()) return;
        const t = ev.target as HTMLElement;
        const anyOpen = document.querySelector('li.menu.nav-item.open-flyout');
        if (!anyOpen) return;

        // if click is inside an open item or its flyout, keep it open
        const openLi = anyOpen as HTMLElement;
        if (openLi.contains(t)) return;

        // otherwise close all
        this.closeAllFlyouts();
    };

    async signOut() {
        const canLeave = await this.componentDeactivateService.confirmLeave();
        if (canLeave) {
            this.authService.logout();
        }
    }

    ngOnDestroy(): void {
        if (typeof document !== 'undefined') {
            document.removeEventListener('click', this.handleDocumentClick, true);
        }
    }
}
